/**
 * Чистая логика превью объединения партнёров (без React).
 *
 * Раскладывает ответ getMergePreview на секции модалки: переносимые строки,
 * предупреждения (конфликты — НЕ переносятся и не входят в итог) и итоговые
 * счётчики. Вынесена из компонента для unit-тестирования.
 */

/** Ответ PartnerRepository.getMergePreview. */
export type MergePreviewData = {
  counts: Record<string, number>;
  /** Подписи переносимых счётчиков merge-обработчиков (сервер). */
  labels: Record<string, string>;
  /** Подписи счётчиков-предупреждений: не переносится, останется у дубля. */
  warningLabels: Record<string, string>;
};

export type MergePreviewRow = { key: string; label: string; count: number };

export type MergePreviewView = {
  /** Секция «Будет перенесено» — только ненулевые счётчики. */
  transferRows: MergePreviewRow[];
  /** Секция «Не будет перенесено (конфликты)» — только ненулевые. */
  warningRows: MergePreviewRow[];
  /** Итог переносимого: без notes и без счётчиков-предупреждений. */
  totalCount: number;
  /** Итог конфликтов. */
  warningCount: number;
  /** Есть реквизиты/описание для дополнения мастера. */
  hasNotes: boolean;
};

/**
 * Собирает представление превью.
 *
 * @param preview - ответ сервера
 * @param baseLabels - подписи базовых счётчиков CRM (живут на клиенте);
 *   серверные labels обработчиков дополняют их
 */
export function buildMergePreviewView(
  preview: MergePreviewData,
  baseLabels: Record<string, string>
): MergePreviewView {
  const warningLabels = preview.warningLabels ?? {};
  const transferLabels: Record<string, string> = { ...baseLabels, ...preview.labels };

  // Строки строятся от counts, а не от подписей: счётчик без подписи
  // отображается с ключом вместо label — данные не должны быть невидимыми,
  // а totalCount всегда равен сумме показанных строк.
  const transferRows = Object.entries(preview.counts)
    .filter(([key]) => key !== 'notes' && !(key in warningLabels))
    .map(([key, count]) => ({ key, label: transferLabels[key] ?? key, count }))
    .filter(row => row.count > 0);

  const warningRows = Object.entries(warningLabels)
    .map(([key, label]) => ({ key, label, count: preview.counts[key] ?? 0 }))
    .filter(row => row.count > 0);

  const totalCount = transferRows.reduce((sum, row) => sum + row.count, 0);

  const warningCount = warningRows.reduce((sum, row) => sum + row.count, 0);

  return {
    transferRows,
    warningRows,
    totalCount,
    warningCount,
    hasNotes: (preview.counts.notes ?? 0) > 0,
  };
}
